import type { IAuditRepository, PaginatedResponse } from '@yawi/domain';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { AuditAction, AuditExportParams, AuditFilter, AuditStatsItem, SystemAuditLog } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiAuditLogResponse {
  id: string;
  actorId?: string | null;
  actorName: string;
  action: string;
  resource: string;
  resourceId?: string | null;
  details?: unknown | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  timestamp: string;
}

// Mapea la respuesta de la API al tipo SystemAuditLog interno
const mapApiAuditLogToAuditLog = (apiLog: ApiAuditLogResponse): SystemAuditLog => ({
  id: apiLog.id,
  timestamp: apiLog.timestamp,
  actorId: apiLog.actorId || '',
  actorName: apiLog.actorName,
  action: apiLog.action as AuditAction,
  resource: apiLog.resource,
  resourceId: apiLog.resourceId || undefined,
  details: typeof apiLog.details === 'string' ? apiLog.details : JSON.stringify(apiLog.details || {}),
  ipAddress: apiLog.ipAddress || undefined,
});

export class ApiAuditRepository implements IAuditRepository {
  async getPaginatedLogs(filter?: AuditFilter): Promise<PaginatedResponse<SystemAuditLog>> {
    const client = getApiClient();

    // Construir query params
    const params = new URLSearchParams();
    params.set('page', String(filter?.page ?? DEFAULT_PAGE));
    params.set('limit', String(filter?.limit ?? DEFAULT_PAGE_SIZE));

    if (filter?.actorId) params.set('actorId', filter.actorId);
    if (filter?.action) params.set('action', filter.action);
    if (filter?.resource) params.set('resource', filter.resource);
    if (filter?.dateFrom) params.set('dateFrom', filter.dateFrom);
    if (filter?.dateTo) params.set('dateTo', filter.dateTo);

    const response = await client.get<PaginatedResponse<ApiAuditLogResponse>>(
      `v1/audit?${params.toString()}`
    );
    return {
      ...response,
      data: response.data.map(mapApiAuditLogToAuditLog),
    };
  }

  async getLogsByActor(actorId: string): Promise<SystemAuditLog[]> {
    const client = getApiClient();
    const response = await client.get<ApiAuditLogResponse[]>(`v1/audit/actor/${actorId}`);
    return response.map(mapApiAuditLogToAuditLog);
  }

  async getLogsByResource(resource: string, resourceId: string): Promise<SystemAuditLog[]> {
    const client = getApiClient();
    const response = await client.get<ApiAuditLogResponse[]>(
      `v1/audit/resource/${resource}/${resourceId}`
    );
    return response.map(mapApiAuditLogToAuditLog);
  }

  async getStats(dateFrom: string, dateTo: string): Promise<AuditStatsItem[]> {
    const client = getApiClient();
    const params = new URLSearchParams({ dateFrom, dateTo });
    const response = await client.get<AuditStatsItem[]>(`v1/audit/stats?${params.toString()}`);
    return response;
  }

  async exportTxt(params: AuditExportParams): Promise<Blob> {
    const client = getApiClient();
    const queryParams = new URLSearchParams({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    });
    if (params.resource) queryParams.set('resource', params.resource);
    if (params.action) queryParams.set('action', params.action);

    const response = await client.get<Blob>(
      `v1/audit/export/txt?${queryParams.toString()}`,
      { responseType: 'blob' }
    );
    return response;
  }
}

